define('presentation/settings/billing/plans/facades/plansFacade',[
    'i18next',
    'businessServices/converters/currencyFormatter',
    'externalDependencies/clientWebSocket',
    'persistence/webSocket/webSocketApp',
    'presentation/settings/billing/plans/presentationObjects/planPresentationObject'
], function () {
    const _i18n = require('i18next');

    const PlanPresentationObjectConstructor = require('presentation/settings/billing/plans/presentationObjects/planPresentationObject');

    const CurrencyFormatterConstructor = require('businessServices/converters/currencyFormatter');
    const _currencyFormatter = new CurrencyFormatterConstructor();

    const clientWebSocket = require('externalDependencies/clientWebSocket');
    const _accountPlanWebSocket = clientWebSocket.forApplication("accountPlan");

    const _billingPackageTypes = require('constants/billingPackageTypeConstants');

    let _promiseFactory = null;
    let _webSocketApp = null;

    const UNLIMITED_VALUE = 999999;
    const UNLIMITED_I18N_VALUE = _i18n.t('plans:unlimited');

    const _getReadableValue = (value) => value === UNLIMITED_VALUE ? UNLIMITED_I18N_VALUE : value;

    const _getPackageKey = (billingPackage) => {
        switch (billingPackage.type) {
            case _billingPackageTypes.telephony.id:
                return _billingPackageTypes.telephony.key;
            case _billingPackageTypes.liveReceptionist.id:
                return _billingPackageTypes.liveReceptionist.key;
            case _billingPackageTypes.webChat.id:
                return _billingPackageTypes.webChat.key;
            case _billingPackageTypes.outboundCalling.id:
                return _billingPackageTypes.outboundCalling.key;
            default:
                return;
        }
    };

    const _getPackageWithLowestMeteredCost = (planPresentationObjects, key) =>{
        if (!planPresentationObjects || planPresentationObjects.length === 0) {
            return null;
        }

        const filteredPlanPresentationObjects = planPresentationObjects .filter((p) => {
            return p.name !== _i18n.t('plans:labelNone') && (p[key] !== null && p[key] !== UNLIMITED_VALUE);
        });
        
        const lowestCost = filteredPlanPresentationObjects.reduce((lowest, current) => {
            const currentCost = current[key];
            const lowestCost = lowest[key];
            return currentCost < lowestCost ? current : lowest;
        }, filteredPlanPresentationObjects[0]);

        return lowestCost;
    };

    const _addMetadataEntry = (billingPackage, includedItem, additionalItem = null) => {
        const includedItemsCount = billingPackage[includedItem];
        const pricePerAdditionalItem = additionalItem ? billingPackage[additionalItem] : null;

        const includedItemsMessage = includedItem === 'includedMinutes' ?
            _i18n.t(`plans:${includedItem}`, { count: _getReadableValue(includedItemsCount), context: _getPackageKey(billingPackage) }) :
            _i18n.t(`plans:${includedItem}`, { count: _getReadableValue(includedItemsCount) });

        if (includedItemsCount === UNLIMITED_VALUE) {
            billingPackage.metadata.push(includedItemsMessage);
            return;
        }

        const pricePerAdditionalItemMessage =  _i18n.t(`plans:${additionalItem}`, { price: _currencyFormatter.formatUSDForDisplay(pricePerAdditionalItem) });
        billingPackage.metadata.push(`${includedItemsMessage} ${pricePerAdditionalItemMessage}`);
    };

    const _buildEmptyPlanPresentationObject = (type) => {
        const planPresentationObject = new PlanPresentationObjectConstructor();
        planPresentationObject.name = _i18n.t('plans:labelNone');
        planPresentationObject.type(type);
        planPresentationObject.isActive(false);
        return planPresentationObject;
    };

    const _buildPlanPresentationObject = (allPackages, billingPackage, isOnboardingComplete, isSignatureService) => {
        const planPresentationObject = new PlanPresentationObjectConstructor();
        planPresentationObject.billingPackageId = billingPackage.billingPackageId;
        planPresentationObject.packageId = billingPackage.packageId;
        planPresentationObject.type(billingPackage.type);
        planPresentationObject.name = billingPackage.name;
        planPresentationObject.price = _currencyFormatter.formatUSDForDisplay(billingPackage.pricePerMonth);
        planPresentationObject.isActive(true);
        planPresentationObject.isRetired(billingPackage.isRetired);
        planPresentationObject.isOnboardingRequired(billingPackage.isOnboardingRequired);
        planPresentationObject.isOnboardingComplete(isOnboardingComplete);
        planPresentationObject.isSignatureService(isSignatureService);

        if (billingPackage.includedChatSessions != null) {
            planPresentationObject.includedChatSessions = billingPackage.includedChatSessions;
            planPresentationObject.pricePerAdditionalChatSession = billingPackage.pricePerAdditionalChatSession;
            _addMetadataEntry(planPresentationObject, "includedChatSessions", "pricePerAdditionalChatSession");
        }

        if (billingPackage.includedMinutes != null) {
            planPresentationObject.includedMinutes = billingPackage.includedMinutes;
            planPresentationObject.pricePerAdditionalMinute = billingPackage.pricePerAdditionalMinute;
            _addMetadataEntry(planPresentationObject, "includedMinutes", "pricePerAdditionalMinute");
        }

        if (billingPackage.includedOutboundContacts != null) {
            planPresentationObject.includedOutboundContacts = billingPackage.includedOutboundContacts;
            planPresentationObject.pricePerAdditionalOutboundContact = billingPackage.pricePerAdditionalOutboundContact;
            _addMetadataEntry(planPresentationObject, "includedOutboundContacts", "pricePerAdditionalOutboundContact");
        }

        if (billingPackage.includedScripts != null) {
            planPresentationObject.includedScripts = billingPackage.includedScripts;
            planPresentationObject.pricePerAdditionalScript = billingPackage.pricePerAdditionalScript;
            _addMetadataEntry(planPresentationObject, "includedScripts", "pricePerAdditionalScript");
        }

        planPresentationObject.includedPhoneNumbers = billingPackage.includedPhoneNumbers;
        planPresentationObject.pricePerAdditionalPhoneNumber = billingPackage.pricePerAdditionalPhoneNumber;

        planPresentationObject.includedUsers = billingPackage.includedUsers;
        planPresentationObject.pricePerAdditionalUser = billingPackage.pricePerAdditionalUser;

        return planPresentationObject;
    };

    const _preparePlansForDisplay = (accountPlan) => {
        const {
            telephonyPackage,
            liveReceptionistPackage,
            isLiveReceptionistOnboardingComplete,
            isLiveReceptionistSignatureService,
            isLiveReceptionistAvailable,
            webChatPackage,
            isWebChatOnboardingComplete,
            isWebChatAvailable,
            outboundCallingPackage,
            isOutboundCallingOnboardingComplete,
            isOutboundCallingAvailable,
        } = accountPlan;

        const allAccountPlans = [telephonyPackage, liveReceptionistPackage, webChatPackage, outboundCallingPackage];
        const showFlexPlan = liveReceptionistPackage === null && webChatPackage === null && outboundCallingPackage === null;

        const displayTelephonyPackage = _buildPlanPresentationObject(allAccountPlans, telephonyPackage, true, false);
        const displayLiveReceptionistPackage = liveReceptionistPackage ?
            _buildPlanPresentationObject(allAccountPlans, liveReceptionistPackage, isLiveReceptionistOnboardingComplete, isLiveReceptionistSignatureService) :
            isLiveReceptionistAvailable === true ? _buildEmptyPlanPresentationObject(_billingPackageTypes.liveReceptionist.id) : null;
        const displayWebChatPackage = webChatPackage ?
            _buildPlanPresentationObject(allAccountPlans, webChatPackage, isWebChatOnboardingComplete, false) :
            isWebChatAvailable === true ? _buildEmptyPlanPresentationObject(_billingPackageTypes.webChat.id) : null;
        const displayOutboundCallingPackage = outboundCallingPackage ?
            _buildPlanPresentationObject(allAccountPlans, outboundCallingPackage, isOutboundCallingOnboardingComplete, false) :
            isOutboundCallingAvailable === true ? _buildEmptyPlanPresentationObject(_billingPackageTypes.outboundCalling.id) : null;

        let displayPlans = [];

        if (displayLiveReceptionistPackage !== null)
        {
            displayPlans.push(displayLiveReceptionistPackage);
        }
        if (displayWebChatPackage !== null)
        {
            displayPlans.push(displayWebChatPackage);
        }
        if (displayOutboundCallingPackage !== null)
        {
            displayPlans.push(displayOutboundCallingPackage);
        }

        if (showFlexPlan) {
            displayPlans.unshift(displayTelephonyPackage);
        }

        const lowestCostPhoneNumberPlan = _getPackageWithLowestMeteredCost(displayPlans, "pricePerAdditionalPhoneNumber" );
        if (lowestCostPhoneNumberPlan) {
            _addMetadataEntry(lowestCostPhoneNumberPlan, "includedPhoneNumbers", "pricePerAdditionalPhoneNumber");
        }

        const lowestCostUserPlan = _getPackageWithLowestMeteredCost(displayPlans, "pricePerAdditionalUser" );
        if (lowestCostUserPlan) {
            _addMetadataEntry(lowestCostUserPlan, "includedUsers", "pricePerAdditionalUser");
        }

        return displayPlans;
    };

    const _getAccountPlans = () => _promiseFactory.defer((promise) => {
        _accountPlanWebSocket.send("getAccountPlan", {}, (response) => {
            const billingPackages = _preparePlansForDisplay(response);
            promise.resolve(billingPackages);
        });
    });

    const _getConstraints = (billingPackageId) => {
        return _promiseFactory.defer((promise) => {
            _accountPlanWebSocket.send("getConstraints", { billingPackageId }, (response) => {
                promise.resolve(response);
            });
        });
    };

    const _activatePlan = (packageId, packageType) => {
        return _promiseFactory.deferIndefinitely((promise) => {
            const request = {
                packageId,
                packageType
            };
            _webSocketApp.send("activate_package", request, (result) => {
                promise.resolve(result);
            });
        });
    };

    const _init = (promiseFactory) => {
        const WebSocketAppConstructor = require('persistence/webSocket/webSocketApp');
        _webSocketApp = new WebSocketAppConstructor();
        _webSocketApp.init("account_plan");

        _promiseFactory = promiseFactory;
    };

    return function () {
        let self = this;

        self.init = _init;
        self.getAccountPlans = _getAccountPlans;
        self.getConstraints = _getConstraints;
        self.activatePlan = _activatePlan;
    };
});
